import { FilterItem, FilterSection } from '~/types/Shared'

export interface FilterItemPosition {
  sectionIndex: number
  rowIndex: number
  itemIndex: number
}

export interface ExclusiveFilterTitles {
  singleSelectSectionTitle: string
  durationSectionTitle: string
  longerThanRowTitle: string
  shorterThanRowTitle: string
}

/**
 * Identifies a filter item by section, row, title, and data together.
 * Titles alone collide (e.g. two users named "John Smith"), and data alone
 * collides too (e.g. both Call Trait items carry `data: true`).
 */
export const getFilterItemKey = (
  sectionTitle: string | undefined,
  rowTitle: string | undefined,
  item: Pick<FilterItem, 'title' | 'data'>,
): string =>
  JSON.stringify([
    sectionTitle ?? '',
    rowTitle ?? '',
    item.title ?? '',
    item.data ?? null,
  ])

const cloneSection = <T>(section: FilterSection<T>): FilterSection<T> => ({
  ...section,
  rows: section.rows.map((row) => ({
    ...row,
    items: row.items.map((item) => ({ ...item })),
  })),
})

const applyDurationConstraints = <T>(
  section: FilterSection<T>,
  titles: ExclusiveFilterTitles,
) => {
  const longerThanRow = section.rows.find(
    (row) => row.title === titles.longerThanRowTitle,
  )
  const shorterThanRow = section.rows.find(
    (row) => row.title === titles.shorterThanRowTitle,
  )
  if (!longerThanRow || !shorterThanRow) return

  const selectedLongerThanIndex = longerThanRow.items.findIndex(
    (item) => item.isSelected,
  )
  const selectedShorterThanIndex = shorterThanRow.items.findIndex(
    (item) => item.isSelected,
  )

  shorterThanRow.items.forEach((shorterItem, index) => {
    if (selectedLongerThanIndex !== -1 && selectedLongerThanIndex >= index) {
      shorterItem.isDisabled = true
      shorterItem.isSelected = false
    } else {
      shorterItem.isDisabled = false
    }
  })

  longerThanRow.items.forEach((longerItem, index) => {
    if (selectedShorterThanIndex !== -1 && selectedShorterThanIndex <= index) {
      longerItem.isDisabled = true
      longerItem.isSelected = false
    } else {
      longerItem.isDisabled = false
    }
  })
}

/**
 * Returns new sections with the item at `position` toggled. The input is not
 * mutated. Returns the original array if the position is out of range.
 */
export const toggleFilterItemAtPosition = <T>(
  sections: FilterSection<T>[],
  { sectionIndex, rowIndex, itemIndex }: FilterItemPosition,
  titles: ExclusiveFilterTitles,
): FilterSection<T>[] => {
  if (!sections[sectionIndex]?.rows[rowIndex]?.items[itemIndex]) {
    return sections
  }

  const updatedSections = sections.map((section, index) =>
    index === sectionIndex ? cloneSection(section) : section,
  )
  const section = updatedSections[sectionIndex]
  const row = section.rows[rowIndex]
  const clickedItem = row.items[itemIndex]

  clickedItem.isSelected = !clickedItem.isSelected

  switch (section.title) {
    case titles.singleSelectSectionTitle:
      row.items.forEach((item, index) => {
        if (index !== itemIndex) {
          item.isSelected = false
        }
      })
      break
    case titles.durationSectionTitle:
      applyDurationConstraints(section, titles)
      break
    default:
      break
  }

  return updatedSections
}
